import { customHeaders, requestApi } from 'api/api'
import { v4 as uuidv4 } from 'uuid'
import { convertPayloadToTransfer } from 'pages/Wallets/components/ConvertV2/adapters/convertPayloadToTransfer'
import { transferToQuote } from 'pages/Wallets/components/ConvertV2/adapters/transferToQuote'
import { CONVERT_QUOTE_VALIDITY_DURATION } from 'pages/Wallets/components/ConvertV2/constants'
import { TotalBalance, WalletBalance } from 'pages/Wallets/types'
import { DepositBankAccount } from 'types/BankAccountsDetails'
import { BeneficiaryTransferDestination } from 'types/beneficiaries'
import { DepositDetailsReq } from 'types/deposit/DepositDetailsReq'
import { AcceptQuote, Quote } from 'types/payments'
import { BankAccount, CurrencyLimits, Paging, WalletType } from 'types/types'
import {
  AcceptQuoteActionRequest,
  ConvertTransferResponse,
  CurrencyInterface,
  PayBeneficiaryRequest,
  PayFee,
  PayoutsRequest,
  postConvert,
  quoteEstimate,
  RefreshQuoteActionRequest,
  TWalletType,
  WalletDescription,
  WalletLifecycleActionRequest,
  WalletsCreateRequest,
  WalletsWithdrawResponse,
} from 'types/wallets'

export const getPaymentMethods = (): Promise<BankAccount[]> => {
  return requestApi({
    url: '/payment-method',
    method: 'GET',
    params: {
      max: 1000,
    },
  })
}

export const getDepositAccounts = (walletId: string): Promise<DepositBankAccount[]> => {
  return requestApi({
    url: `/v2/wallet/${walletId}/deposit-options`,
    method: 'GET',
  })
}

export const getWalletLimits = (
  currencyCode: string,
  walletId?: number
): Promise<CurrencyLimits> => {
  return requestApi({
    url: '/v1/limit',
    method: 'GET',
    params: {
      currency: currencyCode,
      ...(!!walletId && { wallet: walletId }),
    },
  })
}

export const getPayFees = (walletId: number, amount?: number): Promise<PayFee> => {
  return requestApi({
    url: `/wallet/withdraw/${walletId}/fee`,
    method: 'GET',
    params: {
      transactionType: 'third-party',
      ...(!!amount && { amount }),
    },
  })
}

export const getPayoutFees = (
  walletId: number,
  amount?: number,
  isThirdPartyPayment = false,
  transferDestination?: BeneficiaryTransferDestination
): Promise<PayFee> => {
  return requestApi({
    url: `/wallet/withdraw/${walletId}/fee`,
    method: 'GET',
    params: {
      ...(isThirdPartyPayment && { transactionType: 'third-party' }),
      ...(!!amount && { amount }),
      ...(!!transferDestination && { transferDestination }),
    },
  })
}

export const getWithdrawalFee = (walletId: number, amount?: number): Promise<PayFee> => {
  return requestApi({
    url: `/wallet/withdraw/${walletId}/fee`,
    method: 'GET',
    params: {
      ...(!!amount && { amount }),
    },
  })
}

export const postWithdraw = ({
  walletId,
  requestData,
}: PayoutsRequest): Promise<WalletsWithdrawResponse> => {
  return requestApi({
    url: `/wallet/withdraw/${walletId}`,
    method: 'POST',
    data: requestData,
  })
}

export const postPay = ({ walletId, payRequest }: PayBeneficiaryRequest) => {
  return requestApi({
    url: `/wallet/send/${walletId}`,
    method: 'POST',
    data: payRequest,
  })
}

export const createWalletV1 = (payload: WalletsCreateRequest): Promise<WalletType> => {
  const { currency, description } = payload
  return requestApi({
    url: '/wallet',
    method: 'POST',
    data: {
      currency,
      description,
    },
  })
}

export const createWalletV2 = async (payload: WalletsCreateRequest): Promise<WalletType> => {
  const { currency, description } = payload
  let { profileId } = payload

  if (!profileId) {
    const { getWalletProfilesV2 } = await import('./CustomerService')
    const profilesResponse = await getWalletProfilesV2({
      currencyCodes: [currency],
    })

    const profiles = profilesResponse?.profiles || []

    if (!profiles?.length) {
      throw new Error(`No wallet profile available for currency ${currency.toUpperCase()}`)
    }

    profileId = profiles[0].id
  }

  return requestApi({
    baseUrl: process.env.API_URL,
    url: '/ledger/v2/wallets',
    method: 'POST',
    data: {
      currency: currency.toUpperCase(),
      name: description,
      profileId,
    },
    headers: {
      ...customHeaders,
      'Idempotency-Key': uuidv4(),
    } as any,
  })
}

export const getSupportedCurrencies = (): Promise<CurrencyInterface[]> => {
  return requestApi({
    url: '/currency/deposit',
    method: 'GET',
    params: {
      pagination: false,
    },
  })
}

const postV3Transfer = (url: string, data: object): Promise<ConvertTransferResponse> => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url,
    method: 'POST',
    data,
    headers: {
      ...customHeaders,
      'idempotency-key': uuidv4(),
    } as any,
  })
}

export const postConvertTransferQuote = (props: postConvert): Promise<Quote> => {
  return postV3Transfer(
    '/payment/v3/transfers',
    convertPayloadToTransfer(props, 'QUOTE', CONVERT_QUOTE_VALIDITY_DURATION)
  ).then(transferToQuote)
}

export const postConvertTransferDryRun = (props: postConvert): Promise<Quote> => {
  return postV3Transfer(
    '/payment/v3/transfers/dry-run',
    convertPayloadToTransfer(props, 'QUOTE', CONVERT_QUOTE_VALIDITY_DURATION)
  ).then(transferToQuote)
}

export const postConvertQuote = (props: postConvert): Promise<Quote> => {
  return requestApi({
    url: '/v1/quote',
    method: 'POST',
    data: {
      ...props,
      payInMethod: 'wallet',
      payOutMethod: 'wallet',
      usePayInMethod: null,
      merchantId: null,
    },
  })
}

export const postQuoteEstimate = (props: quoteEstimate): Promise<Quote> => {
  const { from, to } = props

  return requestApi({
    url: '/v1/quote',
    method: 'POST',
    params: {
      estimate: true,
      direction: 'out',
    },
    data: {
      from,
      to,
      payInMethod: 'wallet',
      payOutMethod: 'wallet',
    },
  })
}

export const putAcceptQuote = (quoteId: string): Promise<AcceptQuote> => {
  return requestApi({
    url: `/v1/quote/accept/${quoteId}`,
    method: 'PUT',
    data: {
      successUrl: `${window.location.origin}/wallets/convert/complete/${quoteId}`,
    },
  })
}

export const acceptConvertTransfer = ({
  transferId,
  digest,
}: {
  transferId: string
  digest: string
}): Promise<ConvertTransferResponse> => {
  const data: AcceptQuoteActionRequest = { type: 'ACCEPT_QUOTE', digest }

  return postV3Transfer(`/payment/v3/transfers/${transferId}/actions`, data)
}

export const refreshConvertTransfer = (transferId: string): Promise<Quote> => {
  const data: RefreshQuoteActionRequest = {
    type: 'REFRESH_QUOTE',
    validityDuration: CONVERT_QUOTE_VALIDITY_DURATION,
  }

  return postV3Transfer(`/payment/v3/transfers/${transferId}/actions`, data).then(transferToQuote)
}

export const getWalletById = (walletId: number): Promise<WalletType> => {
  return requestApi({
    url: `/wallet/${walletId}`,
    method: 'GET',
  })
}
export const putWalletDescription = ({
  walletId,
  description,
}: WalletDescription): Promise<WalletType> => {
  return requestApi({
    url: `/wallet/description/${walletId}`,
    method: 'PUT',
    data: {
      description,
    },
  })
}

export const postDepositDetails = ({
  walletId,
  protocol,
}: DepositDetailsReq): Promise<WalletType> => {
  return requestApi({
    url: `/wallet/deposit/${walletId}`,
    method: 'POST',
    params: {
      ...(!!protocol && { protocol }),
    },
    data: {},
  })
}

export const getWallets = (): Promise<WalletType[]> => {
  return requestApi({
    url: '/wallet',
    method: 'GET',
    params: {
      max: 1000,
      order: 'asc',
      sort: 'currency.rank',
    },
  })
}

type GetWalletV2Request = {
  walletType: TWalletType
  searchTerm?: string
  offset?: number
  max?: number
  order?: string
  sort?: string
  includeEmbedded?: boolean
  groupBy?: 'currency'
}

export const getWalletsV2 = async ({
  walletType,
  searchTerm,
  offset,
  max,
  order,
  sort,
  includeEmbedded,
  groupBy,
}: GetWalletV2Request): Promise<{ data: WalletType[]; paging?: Paging }> => {
  const { data, headers } = await requestApi({
    url: '/wallet',
    method: 'GET',
    params: {
      ...(!!walletType && { type: walletType }),
      ...(!!searchTerm && { q: searchTerm }),
      ...(!!offset && { offset }),
      ...(!!max && { max }),
      ...(!!order && { order }),
      ...(!!sort && { sort }),
      ...(!!includeEmbedded && { includeEmbedded }),
      ...(!!groupBy && { groupBy }),
    },
    fullResponse: true,
  })

  return {
    data: data ?? [],
    paging: {
      total: Number(headers['x-api-list-total']),
      offset: Number(headers['x-api-list-start']),
      max: max ?? 10,
    },
  }
}

export const postWalletSearch = (data: object) => {
  return requestApi({
    url: '/search/v1/transaction',
    method: 'POST',
    headers: {
      'X-Proxy-Forwarded-Port': '443',
    },
    data: data,
  })
}

export const getTotalBalance = (baseCurrency: string): Promise<TotalBalance> => {
  return requestApi({
    url: '/wallet/summary/total',
    method: 'GET',
    params: {
      ...(baseCurrency && { base: baseCurrency }),
    },
  })
}

export const getWalletBalances = (): Promise<WalletBalance[]> => {
  return requestApi({
    url: '/wallet/balances',
    method: 'GET',
  })
}

export const validateReference = ({ reference }: { reference?: string }) => {
  return requestApi({
    url: `/wallet/withdrawal/request/validate`,
    method: 'POST',
    params: {
      paymentReference: reference,
    },
    headers: {
      'X-Proxy-Forwarded-Port': '443',
    },
  })
}

export const getWalletLegacyId = (walletLsid: string): Promise<number> => {
  return requestApi({
    url: `/v2/wallet/${walletLsid}/convert-id`,
    method: 'GET',
  })
}

export const postWalletAction = ({ walletId, action, comment }: WalletLifecycleActionRequest) => {
  return requestApi({
    baseUrl: process.env.API_URL,
    url: `/ledger/v1/wallets/${walletId}/actions`,
    method: 'POST',
    data: { action, comment },
  })
}
